import { getCountryFromDomain } from "./getCountryFromDomain";
import type { TCountry } from "../types/outerLayout/common";
import { FE_DOMAINS as FEDomains } from "../constants/fe-domains";

export { FEDomains };

const country: TCountry = getCountryFromDomain();

const hostName = window.location.hostname;

type TJordanExcludedCountries = Exclude<TCountry, 'JOR'>;

type TBEUrl = TJordanExcludedCountries | "ALPHA" | "BETA";
type TDocumentationUrl = TJordanExcludedCountries | "ALPHA" | "LOCAL_HOST" | "DEFAULT";
type TOldDashboardUrl = TJordanExcludedCountries | "ALPHA" | 'LOCAL_HOST' | "BETA";
const CHECKOUTUrls: { [key in TBEUrl]: string } = {
  EGY: "https://eg.checkout.paymob.com",
  SAU: "https://ksa.checkout.paymob.com",
  ARE: "https://uae.checkout.paymob.com",
  OMN: "https://om.checkout.paymob.com",
  PAK: "https://pk.checkout.paymob.com",
  ALPHA: "https://alpha.checkout.paymob.com",
  BETA: "https://beta.checkout.paymob.com"
};
const BEUrls: { [key in TBEUrl]: string } = {
  EGY: "https://accept.paymob.com",
  SAU: "https://ksa.paymob.com",
  ARE: "https://uae.paymob.com",
  OMN: "https://oman.paymob.com",
  PAK: "https://pakistan.paymob.com",
  ALPHA: "https://accept-alpha.paymob.com",
  BETA: "https://uat-uae.paymob.com"
};

const OLD_DASHBOARD_DOMAINS: { [key in TOldDashboardUrl]: string } = {
  EGY: `https://accept.paymob.com/portal`,
  SAU: `https://ksa.paymob.com/portal`,
  ARE: `https://uae.paymob.com/portal`,
  OMN: `https://oman.paymob.com/portal`,
  PAK: `https://pakistan.paymob.com/portal`,
  ALPHA: `https://accept-alpha.paymob.com/portal`,
  BETA: `https://beta-alpha.paymob.com/portal`,
  LOCAL_HOST: `http://localhost:3000/portal`,
};

export const UNIFIED_DOCS_URL = 'https://developers.paymob.com/'

export const getServerUrl = () => {
  switch (hostName) {
    case FEDomains.EGY:
      return BEUrls.EGY;
    case FEDomains.SAU:
      return BEUrls.SAU;
    case FEDomains.ARE:
      return BEUrls.ARE;
    case FEDomains.OMN:
      return BEUrls.OMN;
    case FEDomains.PAK:
      return BEUrls.PAK;
    case FEDomains.ALPHA:
      return BEUrls.ALPHA;
    case FEDomains.BETA:
      return BEUrls.BETA;
    case FEDomains.LOCAL_HOST:
      return BEUrls.ALPHA;
  }
};

export const getCheckoutUrl = () => {
  switch (hostName) {
    case FEDomains.EGY:
      return CHECKOUTUrls.EGY;
    case FEDomains.SAU:
      return CHECKOUTUrls.SAU;
    case FEDomains.ARE:
      return CHECKOUTUrls.ARE;
    case FEDomains.OMN:
      return CHECKOUTUrls.OMN;
    case FEDomains.PAK:
      return CHECKOUTUrls.PAK;
    case FEDomains.ALPHA:
      return CHECKOUTUrls.ALPHA;
    case FEDomains.BETA:
      return CHECKOUTUrls.BETA;
    case FEDomains.LOCAL_HOST:
      return CHECKOUTUrls.ALPHA;
  }
};

export const getLeadsServerUrl = () => {
  const crmStagingUrl = "https://crm-staging.paymobsolutions.com";
  const salesAppUrl = "https://salesapp.paymobsolutions.com";

  return [FEDomains.LOCAL_HOST, FEDomains.ALPHA, FEDomains.BETA].includes(hostName) ?
    crmStagingUrl
    :
    salesAppUrl;
};

export const getPaymobAppServerUrl = () => {

  const getConfigForDomain = (domain: string) => {
    const config: { [key in string]?: { [key in TJordanExcludedCountries | 'default']?: string } } = {
      [FEDomains.ALPHA]: {
        EGY: "https://merchantappstaging.paymobsolutions.com",
        SAU: "https://merchantappksa.paymobsolutions.com",
        ARE: "https://merchantappuae.paymobsolutions.com",
        OMN: "https://merchantappoman.paymobsolutions.com",
        PAK: "https://merchantapppakistan.paymobsolutions.com",
        default: "https://merchantappstaging.paymobsolutions.com"
      },
      [FEDomains.BETA]: {
        EGY: "https://merchantappstaging.paymobsolutions.com",
        SAU: "https://merchantappksa.paymobsolutions.com",
        ARE: "https://test-uae-app.paymob.com",
        OMN: "https://merchantappoman.paymobsolutions.com",
        PAK: "https://merchantapppakistan.paymobsolutions.com",
        default: "https://merchantappstaging.paymobsolutions.com"
      },
      [FEDomains.LOCAL_HOST]: { default: "https://merchantappstaging.paymobsolutions.com" },
      [FEDomains.EGY]: { default: "https://merchantapp.paymobsolutions.com" },
      [FEDomains.SAU]: { default: "https://ksa-merchant-backend.paymob.com" },
      [FEDomains.ARE]: { default: "https://uae-merchant-backend.paymob.com" },
      [FEDomains.OMN]: { default: "https://oman-merchant-backend.paymob.com" },
      [FEDomains.PAK]: { default: "https://merchantapppakistan.paymob.com" }
    };

    return config[domain];
  };

  const config = getConfigForDomain(hostName);

  return config && (config[country] || config.default);
};

export const getOldDashboardUrl = () => {
  switch (hostName) {
    case FEDomains.EGY:
      return OLD_DASHBOARD_DOMAINS.EGY;
    case FEDomains.SAU:
      return OLD_DASHBOARD_DOMAINS.SAU;
    case FEDomains.ARE:
      return OLD_DASHBOARD_DOMAINS.ARE;
    case FEDomains.OMN:
      return OLD_DASHBOARD_DOMAINS.OMN;
    case FEDomains.PAK:
      return OLD_DASHBOARD_DOMAINS.PAK;
    case FEDomains.ALPHA:
      return OLD_DASHBOARD_DOMAINS.ALPHA;
    case FEDomains.BETA:
      return OLD_DASHBOARD_DOMAINS.BETA;
    case FEDomains.LOCAL_HOST:
      return OLD_DASHBOARD_DOMAINS.LOCAL_HOST;
  }
};